import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
﻿import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Separator } from "../ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { cn } from "../ui/utils";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Download, 
  Settings, 
  Play,
  Pause,
  RefreshCw,
  Check,
  X,
  AlertCircle,
  XCircle,
  Globe,
  ChevronsUpDown,
  Plus
} from "lucide-react";
import { 
  usePDFJobApi, 
  useAzureFunctionsPDFJobApi,
  type PDFJobDTO,
  type AzureFunctionsPDFJob,
  type BulkPDFGenerationResponse,
  type OrchestrationStatus,
  type TerminateAllWorkflowsResponse
} from "../../services/apiService";

interface BulkPDFGenerationProps {
  onNavigateBack: () => void;
}

interface PDFJob {
  id: string;
  name: string;
  template: string;
  dataSource: string;
  status: 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  progress: number;
  createdAt: Date;
  completedAt?: Date;
  outputCount: number;
  errorCount: number;
  instanceId?: string; // For Azure Functions orchestration tracking
}

export function BulkPDFGeneration({ onNavigateBack }: BulkPDFGenerationProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [dataFile, setDataFile] = useState<File | null>(null);
  const [jobName, setJobName] = useState<string>("");
  const [outputFormat, setOutputFormat] = useState<string>("pdf");
  const [selectedCultures, setSelectedCultures] = useState<string[]>(["en"]);
  const [cultureComboboxOpen, setCultureComboboxOpen] = useState(false);
  const [cultureSearchQuery, setCultureSearchQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isKillingWorkflows, setIsKillingWorkflows] = useState(false);
  const [jobs, setJobs] = useState<PDFJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const pdfJobApi = usePDFJobApi();
  const azureFunctionsApi = useAzureFunctionsPDFJobApi();

  const templates = [
    { id: "product-catalog", name: "Product Catalog Template", description: "Standard product catalog layout" },
    { id: "customer-report", name: "Customer Report Template", description: "Monthly customer analysis" },
    { id: "invoice", name: "Invoice Template", description: "Standard invoice format" },
    { id: "certificate", name: "Certificate Template", description: "Achievement certificates" },
  ];

  const cultures = [
    { id: "en", name: "English", description: "English (default)" },
    { id: "nl-be", name: "Dutch (Belgium)", description: "Nederlands (België)" },
    { id: "fr-be", name: "French (Belgium)", description: "Français (Belgique)" },
    { id: "nl-nl", name: "Dutch (Netherlands)", description: "Nederlands (Nederland)" },
    { id: "fr-fr", name: "French (France)", description: "Français (France)" },
  ];

  // Filter cultures based on search query
  const filteredCultures = cultures.filter(culture =>
    culture.name.toLowerCase().includes(cultureSearchQuery.toLowerCase()) ||
    culture.description.toLowerCase().includes(cultureSearchQuery.toLowerCase()) ||
    culture.id.toLowerCase().includes(cultureSearchQuery.toLowerCase())
  );

  // Convert API DTO to component interface
  const convertDTOToJob = (dto: PDFJobDTO): PDFJob => ({
    id: dto.id.toString(),
    name: dto.name,
    template: dto.template,
    dataSource: dto.dataSource,
    status: dto.status,
    progress: dto.progress,
    createdAt: new Date(dto.createdAt),
    completedAt: dto.completedAt ? new Date(dto.completedAt) : undefined,
    outputCount: dto.outputCount,
    errorCount: dto.errorCount,
    instanceId: (dto as any).instanceId, // Add instanceId if available
  });

  // Convert Azure Functions orchestration to PDFJob
  const convertOrchestrationToJob = (response: BulkPDFGenerationResponse, originalJobName: string): PDFJob => ({
    id: response.instanceId,
    name: originalJobName,
    template: response.pdfJob.template,
    dataSource: response.pdfJob.dataSource,
    status: 'processing',
    progress: 0,
    createdAt: new Date(response.timestamp),
    outputCount: 0,
    errorCount: 0,
    instanceId: response.instanceId,
  });

  // Load existing jobs on component mount
  useEffect(() => {
    const loadJobs = async () => {
      try {
        const jobsData = await pdfJobApi.getPDFJobs();
        const convertedJobs = jobsData.map(convertDTOToJob);
        // Sort jobs by creation date (newest first)
        const sortedJobs = convertedJobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setJobs(sortedJobs);
      } catch (error) {
        console.error('Error loading PDF jobs:', error);
        toast.error('Failed to load PDF jobs');
      } finally {
        setLoading(false);
      }
    };

    loadJobs();
  }, []);

  // Poll for Azure Functions orchestration updates
  useEffect(() => {
    const processingJobs = jobs.filter(job => job.status === 'processing' && job.instanceId);
    
    if (processingJobs.length === 0) return;

    const interval = setInterval(async () => {
      try {
        const updatedJobs = await Promise.all(
          processingJobs.map(async (job) => {
            if (!job.instanceId) return job;
            
            try {
              const orchestrationStatus = await azureFunctionsApi.getOrchestrationStatus(job.instanceId);
              
              // Update job based on orchestration status
              let updatedJob = { ...job };
              
              switch (orchestrationStatus.runtimeStatus.toLowerCase()) {
                case 'running':
                  updatedJob.status = 'processing';
                  updatedJob.progress = Math.min(90, updatedJob.progress + 10); // Simulate progress
                  break;
                case 'completed':
                  updatedJob.status = 'completed';
                  updatedJob.progress = 100;
                  updatedJob.completedAt = new Date(orchestrationStatus.lastUpdatedAt);
                  // Try to parse output for counts
                  if (orchestrationStatus.output) {
                    try {
                      const outputStr = JSON.stringify(orchestrationStatus.output);
                      const successMatch = outputStr.match(/Success: (\d+)/);
                      const failureMatch = outputStr.match(/Failures: (\d+)/);
                      if (successMatch) updatedJob.outputCount = parseInt(successMatch[1]);
                      if (failureMatch) updatedJob.errorCount = parseInt(failureMatch[1]);
                      
                      // Update data source to include ZIP information when job is completed
                      if (updatedJob.outputCount > 0 && !updatedJob.dataSource.includes('|ZIP:')) {
                        updatedJob.dataSource = `${updatedJob.dataSource}|ZIP:available`;
                      }
                      
                      // If there are errors, mark as completed_with_errors
                      if (updatedJob.errorCount > 0) {
                        updatedJob.status = 'completed' as const; // Keep as completed for UI purposes
                      }
                    } catch {
                      // Ignore parsing errors
                    }
                  }
                  
                  // For completed jobs, always mark as having ZIP available if there are output files
                  if (updatedJob.outputCount > 0 && !updatedJob.dataSource.includes('|ZIP:')) {
                    updatedJob.dataSource = `${updatedJob.dataSource}|ZIP:available`;
                  }
                  break;
                case 'failed':
                case 'terminated':
                  updatedJob.status = 'failed';
                  updatedJob.errorCount = 1;
                  break;
                default:
                  // Keep current status for unknown states
                  break;
              }
              
              return updatedJob;
            } catch (error) {
              console.error(`Error getting status for job ${job.instanceId}:`, error);
              return job; // Return unchanged if status check fails
            }
          })
        );

        setJobs(prevJobs => {
          const newJobs = prevJobs.map(job => {
            const updatedJob = updatedJobs.find(uj => uj.id === job.id);
            return updatedJob || job;
          });
          
          // Sort the jobs again to maintain newest first order
          return newJobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        });

        // Check if any jobs completed and force a re-render
        updatedJobs.forEach(job => {
          const oldJob = jobs.find(j => j.id === job.id);
          if (oldJob?.status === 'processing' && job.status === 'completed') {
            toast.success(`PDF generation completed for "${job.name}"! Generated ${job.outputCount} files.`);
            
            // Force a small delay and then trigger a re-render to ensure download buttons are updated
            setTimeout(() => {
              setJobs(prevJobs => 
                prevJobs.map(j => j.id === job.id ? { ...job } : j)
                  .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
              );
            }, 1000);
          } else if (oldJob?.status === 'processing' && job.status === 'failed') {
            toast.error(`PDF generation failed for "${job.name}".`);
          }
        });

      } catch (error) {
        console.error('Error polling orchestration status:', error);
      }
    }, 3000); // Poll every 3 seconds for more responsive updates

    return () => clearInterval(interval);
  }, [jobs, azureFunctionsApi]);

  // Manual refresh function to reload jobs
  const handleRefreshJobs = async () => {
    setRefreshing(true);
    try {
      const jobsData = await pdfJobApi.getPDFJobs();
      const convertedJobs = jobsData.map(convertDTOToJob);
      // Sort jobs by creation date (newest first)
      const sortedJobs = convertedJobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setJobs(sortedJobs);
      toast.success('Jobs refreshed successfully');
    } catch (error) {
      console.error('Error refreshing PDF jobs:', error);
      toast.error('Failed to refresh PDF jobs');
    } finally {
      setRefreshing(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate that it's an Excel file
      const allowedExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error(`Invalid file type. Please select an Excel file (.xlsx or .xls). Selected: ${fileExtension}`);
        // Clear the file input
        event.target.value = '';
        return;
      }
      
      setDataFile(file);
      toast.success(`Excel file "${file.name}" uploaded successfully`);
    }
  };

  const handleCultureSelect = (cultureId: string) => {
    setSelectedCultures(prev => {
      if (prev.includes(cultureId)) {
        // If removing English and it's the only one, keep it
        if (cultureId === "en" && prev.length === 1) {
          toast.warning("At least one culture must be selected");
          return prev;
        }
        return prev.filter(id => id !== cultureId);
      } else {
        return [...prev, cultureId];
      }
    });
  };

  const removeCulture = (cultureId: string) => {
    setSelectedCultures(prev => {
      if (cultureId === "en" && prev.length === 1) {
        toast.warning("At least one culture must be selected");
        return prev;
      }
      return prev.filter(id => id !== cultureId);
    });
  };

  const handleStartGeneration = async () => {
    if (!selectedTemplate || !dataFile || !jobName) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (selectedCultures.length === 0) {
      toast.error("Please select at least one culture");
      return;
    }

    // Validate that the file is an Excel file
    const allowedExtensions = ['.xlsx', '.xls'];
    const fileExtension = dataFile.name.toLowerCase().substring(dataFile.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Invalid file type. Please select an Excel file (.xlsx or .xls). Selected: ${fileExtension}`);
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('Starting PDF generation with file upload...');
      console.log('Template:', selectedTemplate);
      console.log('Data file:', dataFile.name, `(${(dataFile.size / 1024).toFixed(1)} KB)`);
      console.log('Job name:', jobName);
      console.log('Output format:', outputFormat);
      console.log('Selected cultures:', selectedCultures);
      
      const templateName = templates.find(t => t.id === selectedTemplate)?.name || "Unknown Template";
      
      console.log('Calling Azure Functions GenerateBulkPdfsWithFileUpload endpoint...');

      // Call Azure Functions GenerateBulkPdfsWithFileUpload endpoint with file upload and cultures
      const response = await azureFunctionsApi.generateBulkPdfsWithFileUpload(
        jobName,
        templateName,
        dataFile,
        outputFormat,
        selectedCultures
      );

      console.log('Azure Functions response:', response);

      // Convert response to job and add to list
      const newJob = convertOrchestrationToJob(response, jobName);
      setJobs(prev => {
        const newJobs = [newJob, ...prev];
        // Sort by creation date (newest first)
        return newJobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      });
      
      const cultureList = selectedCultures.map(c => cultures.find(culture => culture.id === c)?.name || c).join(', ');
      toast.success(`PDF generation job "${jobName}" started successfully with file upload! Cultures: ${cultureList}, Instance ID: ${response.instanceId}`);
      
      // Reset form
      setJobName("");
      setSelectedTemplate("");
      setDataFile(null);
      setSelectedCultures(["en"]);
      
      // Reset file input
      const fileInput = document.getElementById('data-file') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
    } catch (error) {
      console.error("Error starting PDF generation with file upload:", error);
      
      // Check if it's a CORS or network error and suggest solutions
      let errorMessage = "Failed to start PDF generation with file upload";
      if (error instanceof Error) {
        console.log('Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
        
        if (error.message.includes('404')) {
          errorMessage = "Azure Functions GenerateBulkPdfsWithFileUpload endpoint not found. Make sure the Azure Functions app is running.";
        } else if (error.message.includes('CORS') || error.message.includes('fetch')) {
          errorMessage = "Cannot connect to Azure Functions. Please check if the Functions app is running and CORS is configured properly.";
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = "Authentication failed. Please make sure you're logged in and have the required permissions.";
        } else if (error.message.includes('400')) {
          errorMessage = "Bad request. Please check that all required fields are filled and the file is a valid Excel file.";
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Test PDF generation without authentication
  const handleTestGeneration = async () => {
    if (!selectedTemplate || !dataFile || !jobName) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate that the file is an Excel file
    const allowedExtensions = ['.xlsx', '.xls'];
    const fileExtension = dataFile.name.toLowerCase().substring(dataFile.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Invalid file type. Please select an Excel file (.xlsx or .xls). Selected: ${fileExtension}`);
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('Starting TEST PDF generation with file upload (no auth)...');
      const templateName = templates.find(t => t.id === selectedTemplate)?.name || "Unknown Template";
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', jobName);
      formData.append('template', templateName);
      formData.append('dataFile', dataFile);
      formData.append('outputFormat', outputFormat);
      formData.append('cultures', selectedCultures.join(','));

      console.log('Calling TEST file upload endpoint:', {
        name: jobName,
        template: templateName,
        fileName: dataFile.name,
        fileSize: dataFile.size,
        outputFormat,
        cultures: selectedCultures
      });

      // Get the Azure Functions URL from environment variables
      const azureFunctionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';
      
      // Call the test endpoint without authentication
      const response = await fetch(`${azureFunctionsUrl}/TestFileUpload`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header for FormData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('Test file upload response:', result);
      
      const cultureList = selectedCultures.map(c => cultures.find(culture => culture.id === c)?.name || c).join(', ');
      toast.success(`TEST file upload successful! File: ${result.originalFileName}, Size: ${result.fileSize} bytes, Extracted ${result.extractedItemsCount} items, Cultures: ${cultureList}`);
      
    } catch (error) {
      console.error("Error in test file upload:", error);
      let errorMessage = "Test file upload failed";
      if (error instanceof Error) {
        errorMessage = `Test Error: ${error.message}`;
      }
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Test Azure Functions connectivity
  const testAzureFunctionsConnection = async () => {
    try {
      // Get the Azure Functions URL from environment variables
      const azureFunctionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';
      
      // First test basic connectivity
      console.log('Testing Azure Functions connectivity...');
      const response = await fetch(`${azureFunctionsUrl}/CorsTest`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Azure Functions connectivity test result:', result);
      
      toast.success("Azure Functions connection test successful!");
    } catch (error) {
      console.error("Azure Functions connection test failed:", error);
      
      // Provide specific error messages based on the error type
      let errorMessage = "Azure Functions connection test failed.";
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
          errorMessage = "Cannot connect to Azure Functions. Make sure the Functions app is running and CORS is configured.";
        } else if (error.message.includes('404')) {
          errorMessage = "Azure Functions endpoint not found. Check if the CorsTest endpoint exists.";
        } else {
          errorMessage = `Connection error: ${error.message}`;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Kill all open workflows
  const handleKillAllWorkflows = async () => {
    if (!window.confirm('Are you sure you want to terminate ALL open workflows? This action cannot be undone.')) {
      return;
    }

    setIsKillingWorkflows(true);
    
    try {
      console.log('Terminating all open workflows...');
      
      const reason = 'Bulk termination requested by user via UI';
      const response = await azureFunctionsApi.terminateAllOpenWorkflows(reason);
      
      console.log('Terminate all workflows response:', response);
      
      // Update local jobs list to reflect terminated status
      setJobs(prevJobs => {
        const updatedJobs = prevJobs.map(job => {
          if (job.status === 'processing' || job.status === 'pending') {
            return {
              ...job,
              status: 'failed' as const,
              progress: 0,
              completedAt: new Date(),
              errorCount: job.errorCount + 1
            };
          }
          return job;
        });
        
        // Maintain sort order (newest first)
        return updatedJobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      });
      
      // Show success message with summary
      const summary = response.summary;
      toast.success(
        `Successfully terminated workflows! Processed: ${summary.totalJobsProcessed}, Terminated: ${summary.jobsMarkedAsTerminated}`,
        { duration: 5000 }
      );
      
      if (summary.errorCount > 0) {
        toast.warning(`${summary.errorCount} errors occurred during termination. Check the console for details.`);
      }
      
    } catch (error) {
      console.error("Error terminating all workflows:", error);
      
      let errorMessage = "Failed to terminate workflows";
      if (error instanceof Error) {
        console.log('Termination error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
        
        if (error.message.includes('404')) {
          errorMessage = "Termination endpoint not found. Make sure the Azure Functions app is running with the latest code.";
        } else if (error.message.includes('CORS') || error.message.includes('fetch')) {
          errorMessage = "Cannot connect to Azure Functions. Please check if the Functions app is running and CORS is configured properly.";
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = "Authentication failed. Please make sure you're logged in and have the required permissions.";
        } else {
          errorMessage = `Termination error: ${error.message}`;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsKillingWorkflows(false);
    }
  };

  // Function to check ZIP availability for a specific job
  const checkZipAvailability = async (job: PDFJob): Promise<boolean> => {
    if (!job.instanceId) return false;
    
    try {
      // Try to get the download URL to check if ZIP is available
      await azureFunctionsApi.getPdfZipDownloadUrl(parseInt(job.id));
      return true;
    } catch (error) {
      console.log(`ZIP not yet available for job ${job.id}:`, error);
      return false;
    }
  };

  // Helper function to check if a job has a downloadable ZIP file
  const hasDownloadableZip = (job: PDFJob): boolean => {
    // For Azure Functions jobs (with instanceId), check if completed and has output
    if (job.instanceId) {
      return (job.status === 'completed' || job.status === 'completed_with_errors') && 
             job.outputCount > 0;
    }
    
    // For regular jobs, check for ZIP marker in dataSource
    return (job.status === 'completed' || job.status === 'completed_with_errors') && 
           job.outputCount > 0 && 
           job.dataSource.includes('|ZIP:');
  };

  // Helper function to extract ZIP URL from job data source
  const getZipUrlFromJob = (job: PDFJob): string | null => {
    if (job.dataSource.includes('|ZIP:')) {
      const parts = job.dataSource.split('|ZIP:');
      if (parts.length > 1) {
        return parts[1];
      }
    }
    return null;
  };

  // Handle ZIP file download
  const handleDownloadZip = async (job: PDFJob) => {
    if (!hasDownloadableZip(job)) {
      toast.error('No ZIP file available for download');
      return;
    }

    try {
      console.log(`Starting download for job ${job.id}: ${job.name}`);
      
      // Show loading toast
      const loadingToast = toast.loading(`Preparing download for "${job.name}"...`);
      
      // Download the ZIP file
      const blob = await azureFunctionsApi.downloadPdfZip(parseInt(job.id));
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const sanitizedJobName = job.name.replace(/[^a-zA-Z0-9\-_]/g, '_');
      const filename = `${sanitizedJobName}_${new Date().toISOString().slice(0, 10)}.zip`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      window.URL.revokeObjectURL(url);
      
      // Update toast
      toast.dismiss(loadingToast);
      toast.success(`ZIP file downloaded successfully: ${filename}`);
      
      console.log(`Download completed for job ${job.id}`);
      
    } catch (error) {
      console.error(`Error downloading ZIP for job ${job.id}:`, error);
      
      let errorMessage = 'Failed to download ZIP file';
      if (error instanceof Error) {
        if (error.message.includes('404')) {
          errorMessage = 'ZIP file not found. The file may have been deleted or is not yet available.';
        } else if (error.message.includes('403') || error.message.includes('401')) {
          errorMessage = 'Access denied. Please make sure you have permission to download this file.';
        } else if (error.message.includes('Job not completed')) {
          errorMessage = 'PDF generation is not yet completed. Please wait for the job to finish.';
        } else {
          errorMessage = `Download error: ${error.message}`;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Get download URL info (for testing/debugging)
  const handleGetDownloadUrl = async (job: PDFJob) => {
    try {
      const response = await azureFunctionsApi.getPdfZipDownloadUrl(parseInt(job.id));
      console.log('Download URL info:', response);
      toast.success('Download URL info logged to console');
    } catch (error) {
      console.error('Error getting download URL:', error);
      toast.error('Failed to get download URL info');
    }
  };

  const getStatusIcon = (status: PDFJob['status']) => {
    switch (status) {
      case 'completed':
        return <Check className="h-4 w-4 text-green-600" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />;
      case 'failed':
        return <X className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status: PDFJob['status']) => {
    const variants = {
      completed: "default" as const,
      completed_with_errors: "secondary" as const,
      processing: "secondary" as const,
      failed: "destructive" as const,
      pending: "outline" as const
    };
    
    const statusText = status === 'completed_with_errors' ? 'completed (with errors)' : status;
    
    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {statusText}
      </Badge>
    );
  };

  // Helper function to truncate long data source names
  const truncateDataSource = (dataSource: string, maxLength: number = 50) => {
    if (dataSource.length <= maxLength) return dataSource;
    
    // If it's a URL, show just the filename part
    if (dataSource.startsWith('http')) {
      try {
        const url = new URL(dataSource);
        const pathParts = url.pathname.split('/');
        const filename = pathParts[pathParts.length - 1];
        if (filename && filename.length > 0) {
          return filename.length > maxLength ? `${filename.substring(0, maxLength - 3)}...` : filename;
        }
      } catch {
        // Fallback to regular truncation if URL parsing fails
      }
    }
    
    return `${dataSource.substring(0, maxLength - 3)}...`;
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="flex h-64 items-center justify-center">
          <RefreshCw className="text-muted-foreground h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">Loading PDF jobs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onNavigateBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-foreground text-2xl font-bold">Bulk PDF Generation</h1>
          <p className="text-muted-foreground">Upload Excel files and generate multiple PDFs using Azure Functions with file upload</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshJobs}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Jobs
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={testAzureFunctionsConnection}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Test Connection
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleKillAllWorkflows}
            disabled={isKillingWorkflows || jobs.filter(job => job.status === 'processing' || job.status === 'pending').length === 0}
            className="flex items-center gap-2"
          >
            {isKillingWorkflows ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Terminating...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                Kill All Workflows
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Configuration Panel - Now on top */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Job Configuration
          </CardTitle>
          <CardDescription>
            Set up your PDF generation job using Azure Functions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div>
              <Label htmlFor="job-name">Job Name *</Label>
              <Input
                id="job-name"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                placeholder="Enter job name"
                disabled={isProcessing}
              />
            </div>

            <div>
              <Label htmlFor="template">Template *</Label>
              <Select 
                value={selectedTemplate} 
                onValueChange={setSelectedTemplate}
                disabled={isProcessing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div>
                        <div className="font-medium">{template.name}</div>
                        <div className="text-muted-foreground text-sm">{template.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="output-format">Output Format</Label>
              <Select value={outputFormat} onValueChange={setOutputFormat} disabled={isProcessing}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="pdf-zip">PDF (Zipped)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="cultures" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              PDF Languages *
            </Label>
            <div className="space-y-3">
              <Popover open={cultureComboboxOpen} onOpenChange={setCultureComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cultureComboboxOpen}
                    className="w-full justify-between"
                    disabled={isProcessing}
                  >
                    <span className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add Language...
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search languages..." 
                      value={cultureSearchQuery}
                      onValueChange={setCultureSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>No languages found.</CommandEmpty>
                      <CommandGroup>
                        {filteredCultures.map((culture) => (
                          <CommandItem
                            key={culture.id}
                            value={`${culture.name} ${culture.description} ${culture.id}`}
                            onSelect={() => {
                              handleCultureSelect(culture.id);
                              setCultureComboboxOpen(false);
                              setCultureSearchQuery("");
                            }}
                            disabled={selectedCultures.includes(culture.id)}
                          >
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <Check
                                  className={cn(
                                    "h-4 w-4",
                                    selectedCultures.includes(culture.id) ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className="font-medium">{culture.name}</span>
                                {selectedCultures.includes(culture.id) && (
                                  <Badge variant="secondary" className="text-xs">Selected</Badge>
                                )}
                              </div>
                              <div className="text-muted-foreground text-xs ml-6">{culture.description}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {selectedCultures.length > 0 && (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-sm">
                    Selected languages ({selectedCultures.length}):
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCultures.map(cultureId => {
                      const culture = cultures.find(c => c.id === cultureId);
                      return (
                        <Badge 
                          key={cultureId} 
                          variant="secondary" 
                          className="text-sm flex items-center gap-1"
                        >
                          {culture?.name || cultureId}
                          <button
                            onClick={() => removeCulture(cultureId)}
                            className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                            disabled={isProcessing}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="text-muted-foreground text-xs">
                Select one or more languages for PDF generation. PDFs will be generated for each selected language.
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="data-file">Excel Data File *</Label>
            <div className="space-y-2">
              <Input
                id="data-file"
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileUpload}
                disabled={isProcessing}
              />
              {dataFile && (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4" />
                  {dataFile.name} ({(dataFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
              <div className="text-muted-foreground text-xs">
                Upload an Excel file (.xlsx or .xls) containing item numbers for PDF generation
              </div>
            </div>
          </div>

          <Separator />

          <div className="flex gap-2">
            <CapabilityButton capability={P.ProductPdfJobCreate}
              onClick={handleStartGeneration}
              disabled={isProcessing || !selectedTemplate || !dataFile || !jobName || selectedCultures.length === 0}
              className="flex-1"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Uploading & Starting...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Excel & Generate PDFs
                </>
              )}
            </CapabilityButton>
            
            <Button 
              onClick={handleTestGeneration}
              disabled={isProcessing || !selectedTemplate || !dataFile || !jobName || selectedCultures.length === 0}
              variant="outline"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Test Upload
                </>
              )}
            </Button>
          </div>
          
          <div className="text-muted-foreground text-xs">
            This will upload your Excel file and call the Azure Functions GenerateBulkPdfsWithFileUpload endpoint. The Excel file should contain item numbers for PDF generation.
          </div>
        </CardContent>
      </Card>

      {/* Jobs List - Now below configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generation Jobs
          </CardTitle>
          <CardDescription>
            Track your PDF generation jobs (Azure Functions orchestrations)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {jobs.map((job) => (
              <div key={job.id} className="rounded-lg border p-4">
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex-1 min-w-0"> {/* Add min-w-0 to allow text truncation */}
                    <div className="mb-1 flex items-center gap-2 flex-wrap">
                      {getStatusIcon(job.status)}
                      <h3 className="font-medium truncate">{job.name}</h3>
                      {getStatusBadge(job.status)}
                      {job.instanceId && (
                        <Badge variant="outline" className="text-xs">
                          ID: {job.instanceId.slice(0, 8)}...
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground space-y-1 text-sm">
                      <div className="truncate">Template: {job.template}</div>
                      <div className="break-all">
                        <span className="inline-block">Data Source: </span>
                        <span 
                          className="inline-block max-w-full"
                          title={job.dataSource} // Show full path on hover
                        >
                          {truncateDataSource(job.dataSource)}
                        </span>
                      </div>
                      <div>Started: {job.createdAt.toLocaleString()}</div>
                      {job.completedAt && (
                        <div>Completed: {job.completedAt.toLocaleString()}</div>
                      )}
                      {job.instanceId && (
                        <div className="truncate">Azure Functions Instance: {job.instanceId}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm flex-shrink-0">
                    <div className="font-medium">{job.outputCount} files</div>
                    {job.errorCount > 0 && (
                      <div className="text-red-600">{job.errorCount} errors</div>
                    )}
                  </div>
                </div>

                {job.status === 'processing' && (
                  <div className="space-y-2">
                    <Progress value={job.progress} className="h-2" />
                    <div className="text-muted-foreground text-sm">
                      {job.progress}% complete (Azure Functions orchestration)
                    </div>
                  </div>
                )}

                {(job.status === 'completed' || job.status === 'completed_with_errors') && (
                  <div className="mt-3 flex gap-2">
                    {hasDownloadableZip(job) ? (
                      <>
                        <Button 
                          size="sm" 
                          variant="default"
                          onClick={() => handleDownloadZip(job)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download ZIP ({job.outputCount} files)
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleGetDownloadUrl(job)}
                          className="flex items-center gap-2"
                        >
                          <FileText className="h-4 w-4" />
                          View Details
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleRefreshJobs()}
                          disabled={refreshing}
                          className="flex items-center gap-2"
                        >
                          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                          Check for ZIP
                        </Button>
                        <Button size="sm" variant="outline" disabled>
                          <FileText className="mr-2 h-4 w-4" />
                          View Report
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}

            {jobs.length === 0 && (
              <div className="text-muted-foreground py-8 text-center">
                <FileText className="mx-auto mb-4 h-12 w-12 opacity-50" />
                <p>No PDF generation jobs yet</p>
                <p className="text-sm">Create your first job using the configuration panel above</p>
                <p className="mt-2 text-xs">Make sure Azure Functions app is running</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
